import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

/**
 * Input Component
 *
 * A polished text input with focus states, icons, and error handling.
 */

const inputSizes = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-3.5 text-sm',
  lg: 'h-12 px-4 text-base',
};

const iconSizes = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

export const Input = forwardRef(function Input(
  {
    type = 'text',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    error,
    success,
    disabled,
    className,
    wrapperClassName,
    ...props
  },
  ref
) {
  const hasLeftIcon = !!Icon;
  const hasRightIcon = !!IconRight || error || success;

  return (
    <div className={cn('relative', wrapperClassName)}>
      {/* Left icon */}
      {Icon && (
        <div
          className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2',
            'text-[var(--color-text-muted)]',
            'pointer-events-none'
          )}
        >
          <Icon className={iconSizes[size]} />
        </div>
      )}

      {/* Input field */}
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn(
          // Base styles
          'w-full',
          'bg-white',
          'border border-[var(--color-border)]',
          'rounded-[var(--radius-md)]',
          'font-[var(--font-body)]',
          'text-[var(--color-text-primary)]',
          'placeholder:text-[var(--color-text-muted)]',

          // Transitions
          'transition-all duration-[var(--duration-fast)]',

          // Focus styles
          'focus:outline-none',
          'focus:border-[var(--color-primary-500)]',
          'focus:ring-2 focus:ring-[var(--color-primary-100)]',

          // Size
          inputSizes[size],

          // Icon padding adjustments
          hasLeftIcon && 'pl-10',
          hasRightIcon && 'pr-10',

          // Error state
          error && [
            'border-[var(--color-error-500)]',
            'focus:border-[var(--color-error-500)]',
            'focus:ring-[var(--color-error-100)]',
          ],

          // Success state
          success && [
            'border-[var(--color-success-500)]',
            'focus:border-[var(--color-success-500)]',
            'focus:ring-[var(--color-success-100)]',
          ],

          // Disabled state
          disabled && [
            'bg-[var(--color-neutral-50)]',
            'cursor-not-allowed',
            'opacity-60',
          ],

          // Hover (when not focused or disabled)
          !disabled && 'hover:border-[var(--color-border-strong)]',

          className
        )}
        {...props}
      />

      {/* Right icon / status icon */}
      {(IconRight || error || success) && (
        <div
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            'pointer-events-none',
            error && 'text-[var(--color-error-500)]',
            success && 'text-[var(--color-success-500)]',
            !error && !success && 'text-[var(--color-text-muted)]'
          )}
        >
          {error ? (
            <ErrorIcon className={iconSizes[size]} />
          ) : success ? (
            <CheckIcon className={iconSizes[size]} />
          ) : IconRight ? (
            <IconRight className={iconSizes[size]} />
          ) : null}
        </div>
      )}
    </div>
  );
});

// Error icon component
const ErrorIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

// Check icon component
const CheckIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

/**
 * Search Input with built-in search icon and clear button
 */
export const SearchInput = forwardRef(function SearchInput(
  { value, onChange, onClear, placeholder = 'Search...', size = 'md', className, ...props },
  ref
) {
  return (
    <div className={cn('relative', className)}>
      {/* Search icon */}
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none">
        <SearchIcon className={iconSizes[size]} />
      </div>

      <input
        ref={ref}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          'w-full',
          'bg-white',
          'border border-[var(--color-border)]',
          'rounded-[var(--radius-lg)]',
          'font-[var(--font-body)]',
          'text-[var(--color-text-primary)]',
          'placeholder:text-[var(--color-text-muted)]',
          'transition-all duration-[var(--duration-fast)]',
          'focus:outline-none',
          'focus:border-[var(--color-primary-500)]',
          'focus:ring-2 focus:ring-[var(--color-primary-100)]',
          'focus:shadow-[var(--shadow-md)]',
          'hover:border-[var(--color-border-strong)]',
          inputSizes[size],
          'pl-11',
          value && 'pr-10'
        )}
        {...props}
      />

      {/* Clear button */}
      {value && onClear && (
        <button
          type="button"
          onClick={onClear}
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            'text-[var(--color-text-muted)]',
            'hover:text-[var(--color-text-secondary)]',
            'transition-colors duration-[var(--duration-fast)]',
            'rounded-full p-0.5',
            'hover:bg-[var(--color-neutral-100)]'
          )}
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

// Search icon
const SearchIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

// Close icon
const CloseIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/**
 * Input with label and error message
 */
export function InputField({
  label,
  error,
  hint,
  required,
  className,
  inputClassName,
  ...props
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label className="block text-sm font-medium text-[var(--color-text-primary)]">
          {label}
          {required && <span className="text-[var(--color-error-500)] ml-0.5">*</span>}
        </label>
      )}
      <Input error={!!error} className={inputClassName} {...props} />
      {(error || hint) && (
        <p
          className={cn(
            'text-xs',
            error ? 'text-[var(--color-error-500)]' : 'text-[var(--color-text-tertiary)]'
          )}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
}

export default Input;
